/**
 * CMS Page Renderer Component
 *
 * A high-level component that renders a complete CMS page structure,
 * handling feature flag filtering, template resolution, and error states.
 */

'use client';

import { HomePage } from '@/@cms/types/entities';
import { getDefaultTemplateValues } from '@/@cms/types/utils';
import React, { useMemo } from 'react';
import CMSComponentRenderer from './CMSComponentRenderer';

/**
 * Props for the CMS Page Renderer component
 */
interface CMSPageRendererProps {
    /**
     * Raw CMS page data from the API
     */
    pageData: any;

    /**
     * User's display name for template resolution
     */
    userName?: string;

    /**
     * Studio URL for template resolution
     */
    studioUrl?: string;

    /**
     * Whether to validate the data structure
     */
    validate?: boolean;

    /**
     * Optional error message displayed when page data is invalid
     */
    errorMessage?: string;

    /**
     * Additional template values for variable resolution
     */
    additionalTemplateValues?: Record<string, string>;
}

/**
 * Component for rendering a complete CMS page
 *
 * Features:
 * - Data validation and error handling
 * - Feature flag filtering
 * - Template variable resolution
 * - Fallback content for error states
 *
 * @param props - CMS page renderer props
 * @returns Rendered CMS page or error state
 *
 * @example
 * ```tsx
 * <CMSPageRenderer
 *   pageData={homeData}
 *   userName={user.displayName}
 *   studioUrl="https://studio.example.com"
 * />
 * ```
 */
const CMSPageRenderer: React.FC<CMSPageRendererProps> = ({
    pageData,
    userName = 'User',
    studioUrl = '',
    validate = true,
    errorMessage = 'Unable to load page content',
    additionalTemplateValues = {},
}) => {
    // Check if feature flags are enabled

    // Process and validate the page data
    const processedData = useMemo(() => {
        if (!pageData) return { valid: false, errors: ['No page data provided'] };

        // If not using feature flags, just validate without filtering
        return { valid: true, data: pageData as HomePage };
    }, [pageData]);

    // Merge default template values with additional ones
    const templateValues = useMemo(() => {
        const defaultValues = getDefaultTemplateValues(userName, studioUrl);
        return { ...defaultValues, ...additionalTemplateValues };
    }, [userName, studioUrl, additionalTemplateValues]);

    // Show error state if validation failed
    if (validate && (!processedData.valid || !processedData.data)) {
        return (
            <div className="cms-error-state p-8 text-center">
                <div className="mx-auto inline-block rounded-lg border border-red-200 bg-red-50 p-6">
                    <h2 className="mb-2 text-xlarge font-semibold text-red-700">{errorMessage}</h2>
                    {'errors' in processedData &&
                        processedData.errors &&
                        processedData.errors.length > 0 && (
                            <div className="mt-2 text-small text-red-600">
                                <p>Details:</p>
                                <ul className="mt-1 list-inside list-disc">
                                    {processedData.errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                </div>
            </div>
        );
    }

    // Render the page with the processed data
    return <CMSComponentRenderer component={processedData.data} templateValues={templateValues} />;
};

export default CMSPageRenderer;
